import React, { useState } from 'react'
import {
  Box,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material'
import ClearIcon from '@mui/icons-material/Clear'
import VisibilityIcon from '@mui/icons-material/Visibility'

/*

Copyright 2022-05-09 por Alfonso Moya Ecomsur Holding S.A ©
Detalle: Se crea componente Table para modularizar el desarrollo
*/

interface DeclareErrorTableProps {
  newErrors: SkuOutOfScopeError[]
  declaredErrors: SkuOutOfScopeError[]
  switchComponent: JSX.Element
  isSKUOrName: boolean
  removeFromList: (skuToRemove: string) => void
  showComments?: boolean
}

interface SkuOutOfScopeError {
  sku: string
  name: string
  quantity: number
  asnId: number
  comment?: string
}

export const DeclareErrorTable: React.FC<DeclareErrorTableProps> = ({
  newErrors,
  declaredErrors,
  removeFromList,
  isSKUOrName,
  switchComponent,
  showComments = false
}) => {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())

  const toggleRow = (sku: string) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(sku)) {
        newSet.delete(sku)
      } else {
        newSet.add(sku)
      }
      return newSet
    })
  }

  const renderRow = (
    { sku, name, quantity: qty, comment: comments }: SkuOutOfScopeError,
    isNewError: boolean
  ) => {
    const hasComments = comments && comments.trim() !== ''
    const isExpanded = expandedRows.has(sku)

    return (
      <React.Fragment key={sku}>
        <TableRow>
          <TableCell align='left'>{isSKUOrName ? name : sku}</TableCell>
          <TableCell align='center'>{qty}</TableCell>
          {showComments && (
            <TableCell align='center'>
              <IconButton
                onClick={() => toggleRow(sku)}
                disabled={!hasComments}
                size='small'
                aria-label='ver comentarios'
              >
                <VisibilityIcon />
              </IconButton>
            </TableCell>
          )}
          <TableCell align='right'>
            <IconButton
              onClick={() => removeFromList(sku)}
              edge='end'
              aria-label='delete'
              disabled={!isNewError}
            >
              <ClearIcon />
            </IconButton>
          </TableCell>
        </TableRow>
        {showComments && hasComments && (
          <TableRow>
            <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={4}>
              <Collapse in={isExpanded} timeout='auto' unmountOnExit>
                <Box margin={1}>
                  <Typography variant='body2' color='textSecondary'>
                    <strong>Comentarios:</strong> {comments}
                  </Typography>
                </Box>
              </Collapse>
            </TableCell>
          </TableRow>
        )}
      </React.Fragment>
    )
  }

  return (
    <TableContainer component={Box} style={{ maxHeight: '200px' }}>
      <Table aria-label='error-list-elements' size='small'>
        <TableHead>
          <TableRow>
            <TableCell align='left'>{switchComponent}</TableCell>
            <TableCell align='center'>Cantidad</TableCell>
            {showComments && <TableCell align='center'>Comentarios</TableCell>}
            <TableCell align='right'>Acción</TableCell>
          </TableRow>
        </TableHead>
        <TableBody style={{ maxHeight: '150px' }}>
          {newErrors.map((error) => renderRow(error, true))}
          {declaredErrors.map((error) => renderRow(error, false))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}
